import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  selectedProducts,
  sessionLanguage,
  storedSession,
} from '../../../redux/mydoc.selector';
import { first } from 'rxjs';
import { IDocumentTypeText } from '../../../models/document/document-type-text.model';
import {
  getValue,
  isDefined,
  isDefinedAndFilledArray,
  SOVerify,
} from '../../utility/verify.lib';
import { IDocumentType } from '../../../models/document/document-type.model';
import {
  setAvailableLanguages,
  setSelectedCadTypes,
  setSelectedDocumentTypes,
  setSelectedProducts,
} from '../../../redux/mydoc.action';
import { ICadType } from '../../../models/document/cad-type.model';
import { cloneDeep, isEqual } from 'lodash';
import { IProductItem } from '../../../models/item/product-item.model';
import { IAvailableDocumentLanguages } from '../../../models/language/available-document-languages.model';
import { ILanguageText } from '../../../models/language/language-text.model';
import { UntypedFormGroup } from '@angular/forms';
import { ICadSimpleType } from '../../../models/document/cad-simple-type.model';

@Injectable({
  providedIn: 'root',
})
export class StoreUpdaterService {
  public session$ = this.store.select(storedSession);
  private sessionLanguage$ = this.store.select(sessionLanguage);
  private selectedProducts$ = this.store.select(selectedProducts);

  public constructor(private store: Store) {}

  public updateDocumentTypesInStore(
    documentTypesArray: IDocumentType[],
    isDocumentTypeChecked: Record<string, boolean>
  ): void {
    this.sessionLanguage$.pipe(first()).subscribe((language: string) => {
      const newDocumentTypes: IDocumentTypeText[] = [];
      const allSelectedIds: string[] = Object.keys(
        isDocumentTypeChecked
      ).filter((key) => isDocumentTypeChecked[key] === true);

      if (isDefinedAndFilledArray(() => documentTypesArray)) {
        documentTypesArray.forEach((docType: IDocumentType) => {
          if (allSelectedIds.includes(getValue(() => docType.docTypeId + ''))) {
            const typeText: IDocumentTypeText = getValue(
              () => docType.text,
              []
            ).find(
              (currentText: IDocumentTypeText) =>
                getValue(() => currentText.language, '').toLowerCase() ===
                language
            );
            if (isDefined(() => typeText)) {
              newDocumentTypes.push(typeText);
            }
          }
        });
      }

      this.store.dispatch(
        setSelectedDocumentTypes({ documentTypes: newDocumentTypes })
      );
    });
  }

  public updateCadTypesInStore(
    selectedCadFileTypeViews: ICadSimpleType[],
    cadFileTypes: ICadType[]
  ): void {
    if (
      isDefinedAndFilledArray(() => selectedCadFileTypeViews) &&
      isDefinedAndFilledArray(() => cadFileTypes)
    ) {
      const newFileTypes: ICadType[] = [];
      selectedCadFileTypeViews.forEach((typeView: ICadSimpleType) => {
        if (isDefinedAndFilledArray(() => typeView.cadFileTypes)) {
          typeView.cadFileTypes.forEach((fileType: string) => {
            cadFileTypes.find((cadType: ICadType) => {
              if (
                getValue(() => typeView.cadFormat) ===
                  getValue(() => cadType.cadFormat) &&
                fileType === getValue(() => cadType.cadFileType)
              ) {
                newFileTypes.push(cadType);
              }
            });
          });
        }
      });

      this.store.dispatch(setSelectedCadTypes({ cadTypes: newFileTypes }));
    }
  }

  public updateSelectedItemsInStore(
    isProductItemChecked: Record<string, boolean>,
    documentItems: IProductItem[]
  ): void {
    const newSelectedItems: IProductItem[] = [];

    if (
      isDefinedAndFilledArray(() => Object.keys(isProductItemChecked)) &&
      isDefinedAndFilledArray(() => documentItems)
    ) {
      documentItems.forEach((item: IProductItem) => {
        if (isProductItemChecked[item?.posNumber] === true) {
          newSelectedItems.push(item);
        }
      });
    }

    this.selectedProducts$
      .pipe(first())
      .subscribe((products: IProductItem[]) => {
        if (!isEqual(products, newSelectedItems)) {
          this.store.dispatch(
            setSelectedProducts({ products: newSelectedItems })
          );
        }
      });
  }

  public storeAvailableLanguages(
    allLanguages: IAvailableDocumentLanguages[]
  ): void {
    if (isDefinedAndFilledArray(() => allLanguages)) {
      const allLanguageTexts: ILanguageText[] = [];
      allLanguages.forEach((docLanguage: IAvailableDocumentLanguages) => {
        if (isDefinedAndFilledArray(() => docLanguage.text)) {
          allLanguageTexts.push(docLanguage.text[0]);
        }
      });
      this.store.dispatch(
        setAvailableLanguages({ languages: allLanguageTexts })
      );
    }
  }
}
